import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ProviderAPIClient, {
  type AddressSuggestion,
  type ResolvedAddress,
} from '@/features/auth/ProviderAPIClient'
import { TextInput } from '@/components/common/Modal'
import { useAddressAutocomplete } from '@/hooks/useAddressAutocomplete'

interface AddressAutocompleteProps {
  /** Label for the street-address field. */
  label?: string
  /** Current street value (controlled). */
  value: string
  /** Fired on each keystroke (updates the street field). */
  onChange: (value: string) => void
  /**
   * Fired when the user picks a suggestion and it resolves to a structured
   * address. The parent fills city/state/zip (and street) from this.
   */
  onSelectAddress: (address: ResolvedAddress) => void
  placeholder?: string
  required?: boolean
  error?: string
  showError?: boolean
}

/**
 * Street-address input with an AWS Location autosuggest dropdown. Typing
 * updates the street field directly; picking a suggestion resolves the full
 * address so the caller can fill the remaining fields. Manual entry always
 * works even if suggestions fail.
 */
export function AddressAutocomplete({
  label = 'Street address',
  value,
  onChange,
  onSelectAddress,
  placeholder,
  required,
  error,
  showError,
}: AddressAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  // Supersede + unmount guard for the resolve call (mirrors the hook's abort logic).
  const resolveAbortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const { suggestions, isLoading, clear } = useAddressAutocomplete(open ? value : '')

  const hasSuggestions = useMemo(
    () => open && suggestions.length > 0,
    [open, suggestions.length]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      resolveAbortRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Reset the active option whenever the visible list changes.
  useEffect(() => {
    setActiveIndex(-1)
  }, [suggestions])

  const handleSelect = useCallback(
    async (s: AddressSuggestion) => {
      setOpen(false)
      clear()
      if (!s.placeId) {
        onChange(s.text)
        return
      }
      // A new selection supersedes any in-flight resolve.
      resolveAbortRef.current?.abort()
      const controller = new AbortController()
      resolveAbortRef.current = controller
      setResolving(true)
      try {
        const resolved = await ProviderAPIClient.getInstance().resolveAddress(
          s.placeId,
          controller.signal
        )
        if (!controller.signal.aborted && mountedRef.current) {
          onSelectAddress(resolved)
        }
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') {
          return
        }
        // Resolution failed — keep the suggestion text so the user isn't blocked.
        if (mountedRef.current) {
          onChange(s.text)
        }
        Sentry.captureException(err, { tags: { operation: 'geo.resolve' } })
      } finally {
        if (!controller.signal.aborted && mountedRef.current) {
          setResolving(false)
        }
      }
    },
    [clear, onChange, onSelectAddress]
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!hasSuggestions) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => (i + 1) % suggestions.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
      } else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault()
        void handleSelect(suggestions[activeIndex])
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    },
    [activeIndex, handleSelect, hasSuggestions, suggestions]
  )

  return (
    <div className="relative" ref={containerRef} onKeyDown={onKeyDown}>
      <TextInput
        label={label}
        value={value}
        onChange={(v) => {
          setOpen(true)
          onChange(v)
        }}
        placeholder={placeholder}
        required={required}
        error={error}
        showError={showError}
        enforceFreeText={false}
      />
      {(hasSuggestions || isLoading || resolving) && (
        <ul
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-border-base bg-white shadow-lg dark:border-gray-600 dark:bg-gray-900"
          role="listbox"
          aria-label="Address suggestions"
        >
          {(isLoading || resolving) && (
            <li className="px-3 py-2 text-sm text-text-body/60 dark:text-gray-400">
              {resolving ? 'Loading address…' : 'Searching…'}
            </li>
          )}
          {!resolving &&
            suggestions.map((s, i) => (
              <li key={`${s.placeId ?? s.text}-${i}`} role="option" aria-selected={i === activeIndex}>
                <button
                  type="button"
                  className={`block w-full px-3 py-2 text-left text-sm text-text-heading hover:bg-background-gray dark:text-white dark:hover:bg-gray-800 ${
                    i === activeIndex ? 'bg-background-gray dark:bg-gray-800' : ''
                  }`}
                  onClick={() => void handleSelect(s)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {s.text}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  )
}
